import { useState } from "react";
import { Receipt, Plus, Send, Download, Banknote, Link2, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API } from "@/App";
import { useLoad, Loading, EmptyState, Panel, DataTable, StatusBadge, fmtDate, gbp, copyLink, origin } from "./shared";
import { ClientSelect } from "./ClientSelect";

const InvoiceDialog = ({ call, onClose, onSaved }) => {
  const [form, setForm] = useState({ client_id: "", billing_type: "one_off", tax_rate: 20, due_date: "", notes: "" });
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: "" }]);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const setItem = (i, k, v) => setItems(list => list.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const subtotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const save = async () => {
    const clean = items.filter(i => i.description && Number(i.unit_price) > 0).map(i => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) }));
    if (!form.client_id || !clean.length) return toast.error("Select a client and add at least one priced line item");
    await call("/invoices", { method: "POST", body: { ...form, tax_rate: Number(form.tax_rate), due_date: form.due_date || null, items: clean } });
    toast.success("Draft invoice created"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle>New Commerce invoice</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Client *</Label><ClientSelect call={call} value={form.client_id} onChange={set("client_id")} testid="invoice-client-select" /></div>
          <div className="space-y-2">
            <Label>Line items</Label>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <Input className="col-span-7" placeholder="Description" value={it.description} onChange={e => setItem(i, "description", e.target.value)} data-testid={`invoice-item-desc-${i}`} />
                <Input className="col-span-2" type="number" min="1" value={it.quantity} onChange={e => setItem(i, "quantity", e.target.value)} data-testid={`invoice-item-qty-${i}`} />
                <Input className="col-span-2" type="number" min="0" placeholder="£" value={it.unit_price} onChange={e => setItem(i, "unit_price", e.target.value)} data-testid={`invoice-item-price-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => setItems(l => l.filter((_, j) => j !== i))} disabled={items.length === 1}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setItems(l => [...l, { description: "", quantity: 1, unit_price: "" }])} data-testid="invoice-add-item"><Plus className="h-3.5 w-3.5 mr-1" />Add line</Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Billing</Label>
              <Select value={form.billing_type} onValueChange={set("billing_type")}>
                <SelectTrigger data-testid="invoice-billing"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="one_off">One-off</SelectItem><SelectItem value="monthly">Monthly retainer</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>VAT %</Label><Input type="number" value={form.tax_rate} onChange={set("tax_rate")} data-testid="invoice-tax" /></div>
            <div className="space-y-1.5"><Label>Due date</Label><Input type="date" value={form.due_date} onChange={set("due_date")} data-testid="invoice-due" /></div>
          </div>
          <div className="space-y-1.5"><Label>Notes (defaults to bank details from settings)</Label><Textarea value={form.notes} onChange={set("notes")} data-testid="invoice-notes" /></div>
          <p className="text-sm text-right text-slate-600">Subtotal {gbp(subtotal)} · Total {gbp(subtotal * (1 + Number(form.tax_rate || 0) / 100))}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} data-testid="invoice-save-btn">Create draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PaymentDialog = ({ call, invoice, onClose, onSaved }) => {
  const [form, setForm] = useState({ amount: (invoice.total - (invoice.amount_paid || 0)).toFixed(2), method: "bank_transfer", reference: "" });
  const save = async () => {
    await call(`/invoices/${invoice.invoice_id}/record-payment`, { method: "POST", body: { ...form, amount: Number(form.amount) } });
    toast.success("Payment recorded"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Record payment — {invoice.invoice_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Amount received (£)</Label><Input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} data-testid="payment-amount" /></div>
          <div className="space-y-1.5"><Label>Method</Label>
            <Select value={form.method} onValueChange={v => setForm(p => ({ ...p, method: v }))}>
              <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank transfer</SelectItem><SelectItem value="card_terminal">Card terminal</SelectItem>
                <SelectItem value="cash">Cash</SelectItem><SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Reference</Label><Input value={form.reference} onChange={e => setForm(p => ({ ...p, reference: e.target.value }))} placeholder="Bank reference" data-testid="payment-reference" /></div>
          <p className="text-xs text-slate-400">Only record money that has actually been received. This updates confirmed revenue.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} data-testid="payment-save-btn">Record payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const downloadPdf = async (token, inv) => {
  const res = await fetch(`${API}/admin/invoices/${inv.invoice_id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
  if (!res.ok) return toast.error("Could not generate PDF");
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click(); URL.revokeObjectURL(url);
};

const InvoiceActions = ({ inv, call, token, reload, onPay }) => {
  const open = !["paid", "cancelled"].includes(inv.status);
  const send = async () => { const r = await call(`/invoices/${inv.invoice_id}/send`, { method: "POST", body: { origin_url: origin() } }); toast.success("Invoice emailed with payment link"); copyLink(r.public_url); reload(); };
  const cancel = async () => { if (!window.confirm("Cancel this invoice?")) return; await call(`/invoices/${inv.invoice_id}/cancel`, { method: "POST" }); reload(); };
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button size="sm" variant="outline" onClick={() => downloadPdf(token, inv)} data-testid={`invoice-pdf-${inv.invoice_id}`}><Download className="h-3.5 w-3.5" /></Button>
      {open && <Button size="sm" variant="outline" onClick={send} data-testid={`invoice-send-${inv.invoice_id}`}><Send className="h-3.5 w-3.5 mr-1" />{inv.status === "draft" ? "Send" : "Resend"}</Button>}
      {open && <Button size="sm" variant="outline" onClick={() => onPay(inv)} data-testid={`invoice-record-payment-${inv.invoice_id}`}><Banknote className="h-3.5 w-3.5 mr-1" />Mark paid</Button>}
      {inv.public_url && open && <Button size="sm" variant="ghost" onClick={() => copyLink(inv.public_url)} data-testid={`invoice-copy-${inv.invoice_id}`}><Link2 className="h-3.5 w-3.5" /></Button>}
      {open && <Button size="sm" variant="ghost" onClick={cancel} data-testid={`invoice-cancel-${inv.invoice_id}`}><X className="h-3.5 w-3.5" /></Button>}
    </div>
  );
};

const Invoices = ({ call, token }) => {
  const { data, loading, reload } = useLoad(call, "/invoices");
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState(null);
  if (loading) return <Loading />;
  const rows = data?.invoices || [];
  return (
    <>
      <div className="flex justify-end mb-3"><Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setCreating(true)} data-testid="new-invoice-btn"><Plus className="h-4 w-4 mr-1" />New invoice</Button></div>
      {rows.length ? (
        <Panel>
          <DataTable testid="invoices-table" rowKey="invoice_id" rows={rows} columns={[
            { key: "invoice_number", label: "Invoice", render: r => <div><p className="font-mono text-xs font-medium">{r.invoice_number}</p><p className="text-xs text-slate-500">{r.client_name}</p></div> },
            { key: "total", label: "Total", render: r => <span>{gbp(r.total)}{r.billing_type === "monthly" && <span className="text-xs text-slate-400"> /mo</span>}</span> },
            { key: "amount_paid", label: "Paid", render: r => gbp(r.amount_paid) },
            { key: "status", label: "Status", render: r => <StatusBadge status={r.status} testid={`invoice-status-${r.invoice_id}`} /> },
            { key: "due_date", label: "Due" },
            { key: "act", label: "", render: r => <InvoiceActions inv={r} call={call} token={token} reload={reload} onPay={setPaying} /> },
          ]} />
        </Panel>
      ) : <EmptyState testid="invoices-empty" icon={Receipt} title="No Commerce invoices yet" text="Draft invoices are created automatically when an agreement is signed, or you can create one manually." />}
      {creating && <InvoiceDialog call={call} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />}
      {paying && <PaymentDialog call={call} invoice={paying} onClose={() => setPaying(null)} onSaved={() => { setPaying(null); reload(); }} />}
    </>
  );
};

const Payments = ({ call }) => {
  const { data, loading } = useLoad(call, "/payments");
  if (loading) return <Loading />;
  const rows = data?.payments || [];
  if (!rows.length) return <EmptyState testid="payments-empty" icon={Banknote} title="No payments recorded yet" text="Stripe-confirmed and manually recorded payments appear here." />;
  return (
    <Panel>
      <DataTable testid="payments-table" rowKey="payment_id" rows={rows} columns={[
        { key: "paid_at", label: "Date", render: r => fmtDate(r.paid_at) },
        { key: "client_name", label: "Client" },
        { key: "invoice_number", label: "Invoice", render: r => <span className="font-mono text-xs">{r.invoice_number}</span> },
        { key: "amount", label: "Amount", render: r => <span className="font-medium text-emerald-700">{gbp(r.amount)}</span> },
        { key: "method", label: "Method", render: r => <span className="capitalize">{r.method.replace("_", " ")}</span> },
        { key: "reference", label: "Reference", render: r => <span className="text-xs text-slate-500 break-all">{r.reference || "—"}</span> },
      ]} />
    </Panel>
  );
};

export const CommerceInvoices = ({ call, token }) => (
  <Tabs defaultValue="invoices" data-testid="commerce-invoices">
    <TabsList>
      <TabsTrigger value="invoices" data-testid="invoices-tab">Invoices</TabsTrigger>
      <TabsTrigger value="payments" data-testid="payments-tab">Payments</TabsTrigger>
    </TabsList>
    <TabsContent value="invoices" className="mt-4"><Invoices call={call} token={token} /></TabsContent>
    <TabsContent value="payments" className="mt-4"><Payments call={call} /></TabsContent>
  </Tabs>
);
